import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { format } from "date-fns";
import "../../Styles/PortfolioBarChart.css";

// This chart visualises the strategy accounting time‑series with percentage returns
// Data comes from /accounting/portfolio via GetAccountingPortfolio
const FinanceGraph = ({ AccountingPortfolioData, selectedTimeRange = "ITD" }) => {
  // Process chart data with comprehensive logic
  const chartData = useMemo(() => {
    // Extract portfolio data from props or Redux state
    const portfolioData = Array.isArray(AccountingPortfolioData) 
      ? AccountingPortfolioData 
      : (AccountingPortfolioData?.dataPoints || AccountingPortfolioData?.datapoints || []);

    if (!Array.isArray(portfolioData) || portfolioData.length === 0) {
      return {
        chartSeries: [],
        xAxisLabels: [],
        chartData: [],
        totalPortfolio: 0,
        percentageChange: 0,
        absoluteChange: 0,
        initialBalance: 0
      };
    }

    // Aggregate data by date (sum endingBalance for all userRoleIds on the same date)
    const aggregatedByDate = portfolioData.reduce((acc, item) => {
      const dateKey = (item.createdAt || item.date || item.timestamp).split('T')[0];
      if (!acc[dateKey]) {
        acc[dateKey] = {
          createdAt: item.createdAt || item.date || item.timestamp,
          endingBalance: 0
        };
      }
      acc[dateKey].endingBalance += parseFloat(item.endingBalance || 0);
      return acc;
    }, {});

    // Convert aggregated object to array
    const aggregatedData = Object.values(aggregatedByDate);

    // Sort data by date
    const sortedData = [...aggregatedData].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateA - dateB;
    });

    // Calculate initial balance - use absolute value if negative or very small
    let initialBalance = parseFloat(sortedData[0]?.endingBalance || 0);
    let baselineIndex = 0;
      
    // Handle edge case: if initial balance is 0, negative, or very small
    if (initialBalance <= 0 || Math.abs(initialBalance) < 1) {
      const firstPositiveIndex = sortedData.findIndex(item => parseFloat(item.endingBalance || 0) > 1);
      if (firstPositiveIndex >= 0) {
        initialBalance = parseFloat(sortedData[firstPositiveIndex].endingBalance);
        baselineIndex = firstPositiveIndex;
      } else {
        initialBalance = Math.abs(initialBalance) || 1;
      }
    }

    // Use all data for returns calculation
    const rangeFilteredData = sortedData;
    
    const rangeInitialBalance = rangeFilteredData.length > 0 
      ? parseFloat(rangeFilteredData[0]?.endingBalance || 0)
      : initialBalance;

    // Calculate percentage change for each data point
    const processedData = sortedData.map((item, index) => {
      const currentBalance = parseFloat(item.endingBalance || 0);
      let percentageChange = 0;
      
      // Calculate percentage relative to our selected baseline
      if (initialBalance !== 0 && Math.abs(initialBalance) >= 0.01) {
        percentageChange = ((currentBalance - initialBalance) / Math.abs(initialBalance)) * 100;
        // Cap extreme percentages to keep chart readable
        percentageChange = Math.max(-99.99, Math.min(999.9, percentageChange));
      }
      
      return {
        date: new Date(item.createdAt || item.date || item.timestamp),
        endingBalance: currentBalance,
        percentageChange: percentageChange,
        rawBalance: currentBalance
      };
    });

    // Get latest portfolio value
    const latest = processedData[processedData.length - 1];
    const totalPortfolio = latest.endingBalance;
    
    // Calculate returns for the selected range
    const rangeLatest = rangeFilteredData.length > 0 
      ? rangeFilteredData[rangeFilteredData.length - 1] 
      : latest;
    const rangeLatestBalance = parseFloat(rangeLatest?.endingBalance || 0);
    const percentageChange = rangeInitialBalance !== 0 
      ? ((rangeLatestBalance - rangeInitialBalance) / rangeInitialBalance) * 100 
      : 0;
    const absoluteChange = rangeLatestBalance - rangeInitialBalance;

    // Use ALL data from API
    const displayData = processedData;

    // Format X-axis labels based on timeframe
    let chartPoints = [];

    if (selectedTimeRange === '5D' || selectedTimeRange === 'MTD') {
      chartPoints = displayData.map(item => ({
        x: format(item.date, 'MM/dd'),
        y: item.percentageChange
      }));
    } else if (selectedTimeRange === 'YTD') {
      chartPoints = displayData.map(item => ({
        x: format(item.date, 'MM/dd'),
        y: item.percentageChange
      }));
    } else if (selectedTimeRange === 'ITD') {
      chartPoints = displayData.map(item => ({
        x: format(item.date, 'MM/dd/yyyy'),
        y: item.percentageChange
      }));
    } else {
      // Custom or default
      chartPoints = displayData.map(item => ({
        x: format(item.date, 'MM/dd'),
        y: item.percentageChange
      }));
    }

    const xAxisLabels = chartPoints.map(point => point.x);

    return {
      chartSeries: [{ name: 'Performance', data: chartPoints }],
      xAxisLabels,
      chartData: chartPoints,
      totalPortfolio,
      percentageChange,
      absoluteChange,
      initialBalance
    };
  }, [AccountingPortfolioData, selectedTimeRange]);

  // Calculate Y-axis range based on actual data
  const yAxisRange = useMemo(() => {
    if (!chartData.chartData || chartData.chartData.length === 0) {
      return { min: -100, max: 100 };
    }
    
    const values = chartData.chartData
      .map(point => point.y)
      .filter(y => y !== null && y !== undefined && isFinite(y));
    
    if (values.length === 0) {
      return { min: -100, max: 100 };
    }
    
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    
    // Add padding (20% on each side)
    const range = maxValue - minValue;
    const padding = range * 0.2 || 10;
    
    // Default to -100/100 if range is reasonable, otherwise adjust
    if (minValue >= -100 && maxValue <= 100) {
      return { min: -100, max: 100 };
    }
    
    return {
      min: Math.floor(minValue - padding),
      max: Math.ceil(maxValue + padding)
    };
  }, [chartData.chartData]);

  const chartOptions = useMemo(() => ({
    chart: {
      height: 360,
      type: "line",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
      colors: ["#1976D2"],
    },
    xaxis: {
      categories: chartData.xAxisLabels,
      labels: {
        style: {
          colors: "#666666",
          fontSize: "12px",
        },
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
    },
    yaxis: {
      min: yAxisRange.min,
      max: yAxisRange.max,
      tickAmount: undefined,
      labels: {
        formatter: function(value) {
          return value?.toFixed(0) || '0';
        },
        style: {
          colors: "#666666",
          fontSize: "12px",
        },
      },
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
    },
    grid: {
      borderColor: "#E0E0E0",
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
          color: "#E0E0E0",
        },
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: true,
      intersect: false,
      style: {
        fontSize: "12px",
      },
      custom: function({ seriesIndex, dataPointIndex, w }) {
        const data = chartData.chartData[dataPointIndex];
        if (!data || data.y === null) return '';
        
        const date = chartData.xAxisLabels[dataPointIndex] || '';
        const percentage = data.y;
        const portfolioValue = chartData.initialBalance * (1 + percentage / 100);
        
        const formatCurrency = (value) => {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        };
        
        const formatPercentage = (value) => {
          return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
        };
        
        return `
          <div style="padding: 8px 12px; background: white; border: 1px solid #E6E6E6; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
            <div style="font-weight: 600; margin-bottom: 4px; color: #141218;">${date}</div>
            <div style="color: #545454; font-size: 12px;">Portfolio Value: <span style="font-weight: 600; color: #141218;">${formatCurrency(portfolioValue)}</span></div>
            <div style="color: #545454; font-size: 12px;">Return: <span style="font-weight: 600; color: ${percentage >= 0 ? '#22c55e' : '#ef4444'};">${formatPercentage(percentage)}</span></div>
          </div>
        `;
      }
    },
    markers: {
      size: 4,
      colors: ["#1976D2"],
      strokeColors: "#FFFFFF",
      strokeWidth: 2,
      hover: {
        size: 7,
      },
    },
    colors: ["#1976D2"],
  }), [chartData, yAxisRange]);

  const series = chartData.chartSeries;

  if (!chartData.chartData || chartData.chartData.length === 0) {
    return (
      <div
        className="finance-graph"
        style={{ 
          position: "relative", 
          width: "100%",
          height: "360px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999999"
        }}
      >
        No data available
      </div>
    );
  }

  return (
    <div
      className="finance-graph"
      style={{ position: "relative", width: "100%" }}
    >
      <ReactApexChart
        options={chartOptions}
        series={series}
        type="line"
        height={360}
      />
    </div>
  );
};

export default FinanceGraph;
